import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, X, Tablet, Clock } from 'lucide-react';

interface TableAction {
  id: string;
  timestamp: string;
  workerName: string;
  action: 'start' | 'stop' | 'activated';
  tableName: string;
  projectName?: string;
  locationName?: string;
}

interface TableActionNotificationProps {
  userId: number;
  userRole: string;
}

const TableActionNotification: React.FC<TableActionNotificationProps> = ({
  userId,
  userRole
}) => {
  const [recentActions, setRecentActions] = useState<TableAction[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const checkForTableActions = () => {
      const storedActions = localStorage.getItem('table-actions');
      if (storedActions) {
        try {
          const actions: TableAction[] = JSON.parse(storedActions);
          const recentActions = actions.filter(action => {
            const actionTime = new Date(action.timestamp);
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
            return actionTime > fiveMinutesAgo;
          });
          
          setRecentActions(recentActions);
          if (recentActions.length > 0) {
            setShowNotifications(true);
          }
        } catch (error) {
          console.error('Error parsing table actions:', error);
        }
      }
    };

    checkForTableActions();
    const interval = setInterval(checkForTableActions, 10000);
    return () => clearInterval(interval);
  }, []);

  const dismissNotification = (actionId: string) => {
    setRecentActions(prev => prev.filter(action => action.id !== actionId));
    if (recentActions.length <= 1) {
      setShowNotifications(false);
    }
  };

  const dismissAll = () => {
    setRecentActions([]);
    setShowNotifications(false);
  };

  if (!showNotifications || recentActions.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <Card className="border-blue-200 bg-blue-50 shadow-lg">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-blue-800">
                Acțiuni Masă
              </span>
              <Badge variant="outline" className="bg-blue-100 text-blue-700">
                {recentActions.length}
              </Badge>
            </div>
            <Button
              onClick={dismissAll}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-2">
            {recentActions.slice(0, 3).map((action) => (
              <div
                key={action.id}
                className="flex items-start justify-between bg-white p-2 rounded border"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Tablet className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">
                      {action.tableName}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800">
                    <strong>{action.workerName}</strong> a{' '}
                    {action.action === 'start' ? 'început' : 
                     action.action === 'stop' ? 'terminat' : 'fost activat la'} lucrul
                  </p>
                  {action.action === 'start' && action.projectName && (
                    <p className="text-xs text-gray-600 mt-1">
                      {action.projectName} - {action.locationName}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-500">
                      {new Date(action.timestamp).toLocaleTimeString('ro-RO')}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => dismissNotification(action.id)}
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-2"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
          
          {recentActions.length > 3 && (
            <p className="text-xs text-blue-600 mt-2 text-center">
              +{recentActions.length - 3} acțiuni în plus
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TableActionNotification;